<template>
  <Dialog
    :visible="modelValue"
    modal
    header="Redeem code"
    :style="{ width: '24rem' }"
    :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
    :draggable="false"
    @update:visible="onVisible"
  >
    <!-- Enter submits; the footer button submits this form by its id -->
    <form
      id="redeem-code-form"
      class="kb-form"
      novalidate
      @submit.prevent="onRedeem"
    >
      <p class="kb-dialog-lead">
        Enter the code you received from your SubKrewe captain. It covers your membership dues and adds you to their SubKrewe.
      </p>

      <p
        v-if="message && messageSeverity === 'error'"
        class="kb-callout-alert m-0"
        role="alert"
      >
        {{ message }}
      </p>

      <KbField
        field-id="redeem-code"
        label="Code"
        :error="codeError"
      >
        <template #default="{ describedBy }">
          <InputText
            id="redeem-code"
            v-model="code"
            size="small"
            fluid
            autocomplete="off"
            autofocus
            :invalid="Boolean(codeError)"
            :aria-describedby="describedBy"
          />
        </template>
      </KbField>

      <p
        v-if="message && messageSeverity !== 'error'"
        class="kb-status-line is-ok"
        role="status"
      >
        {{ message }}
      </p>
    </form>

    <template #footer>
      <Button
        label="Cancel"
        text
        severity="secondary"
        size="small"
        @click="emit('update:modelValue', false)"
      />
      <Button
        type="submit"
        form="redeem-code-form"
        label="Redeem"
        size="small"
        :loading="loading"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { extractFetchErrorMessage } from '~/utils/errorMessages'

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void, (e: 'redeemed', info: { year: string, subkreweId: string }): void }>()

const code = ref('')
const codeError = ref('')
const loading = ref(false)
const message = ref('')
const messageSeverity = ref<'success' | 'error' | 'info' | 'warn'>('info')

watch(() => props.modelValue, (v) => {
  if (!v) {
    code.value = ''
    codeError.value = ''
    message.value = ''
  }
})

// Typing clears the "enter a code" message
watch(code, () => {
  codeError.value = ''
})

function onVisible(v: boolean) {
  emit('update:modelValue', v)
}

async function onRedeem() {
  if (!code.value) {
    codeError.value = 'Enter a code'
    return
  }
  loading.value = true
  message.value = ''
  try {
    const res = await $fetch('/api/pool/redeem', { method: 'POST', body: { code: code.value } }) as { success: boolean, year?: string, subkreweId?: string, message?: string }
    if (res.success) {
      messageSeverity.value = 'success'
      message.value = `Redeemed for ${res.year}`
      emit('redeemed', { year: String(res.year || ''), subkreweId: String(res.subkreweId || '') })
      emit('update:modelValue', false)
    }
    else {
      messageSeverity.value = 'error'
      message.value = res.message || 'Redeem failed'
    }
  }
  catch (e) {
    messageSeverity.value = 'error'
    // The server's reason ("Code not found", "Code already redeemed or
    // unavailable") rides on the fetch error's data, not on its message
    message.value = extractFetchErrorMessage(e, 'Redeem failed')
  }
  finally {
    loading.value = false
  }
}
</script>
